import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

export default function CreateSquarespaceOrderDialog({
  open,
  onClose,
  formData,
  onFormDataChange,
  onCreate,
  creating,
  error,
  /** When provided (e.g. from Customer Detail page), Customer ID is fixed and field is hidden */
  customerId: fixedCustomerId,
  /** When provided (e.g. from Subscription Detail page), shows subscription info panel and pre-fills pricing from plan */
  subscription,
  apiResponse,
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [pricingOptions, setPricingOptions] = React.useState([]);
  const [pricingLoading, setPricingLoading] = React.useState(false);
  const [pricingError, setPricingError] = React.useState(null);

  // Auto-select pricing from subscription plan meta_data when dialog opens with subscription
  React.useEffect(() => {
    if (open && subscription && !formData.pricingId?.trim()) {
      const ssPricingId =
        subscription?.plan_details?.meta_data?.squarespace_pricing_id ||
        subscription?.meta_data?.squarespace_pricing_id;
      if (ssPricingId) {
        onFormDataChange({ ...formData, pricingId: String(ssPricingId) });
      }
    }
  }, [open, subscription, formData, onFormDataChange]);

  // Fetch pricing options when dialog opens
  React.useEffect(() => {
    if (open) {
      const fetchPricing = async () => {
        setPricingLoading(true);
        setPricingError(null);
        try {
          const token = await getAccessTokenSilently().catch(() => null);
          const response = await fetch(`${API_BASE_URL}/admin/squarespace/prices`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch pricing: ${response.status}`);
          }

          const responseData = await response.json();
          const pricingData = Array.isArray(responseData.data) ? responseData.data : (responseData.pricing || []);
          const pricingList = Array.isArray(pricingData) ? pricingData : [];
          
          // Prefer website pricing; fall back to all if none match
          const websitePricing = pricingList.filter(item => 
            item?.type === 'website' || 
            item?.category === 'website' ||
            (item?.type !== 'domain' && !item?.tld && !item?.extension)
          );
          const options = websitePricing.length > 0 ? websitePricing : pricingList;
          setPricingOptions(options);
          // When options load, auto-select from subscription plan if pricingId still empty
          if (subscription && !formData.pricingId?.trim()) {
            const ssPricingId =
              subscription?.plan_details?.meta_data?.squarespace_pricing_id ||
              subscription?.meta_data?.squarespace_pricing_id;
            if (ssPricingId) {
              onFormDataChange({ ...formData, pricingId: String(ssPricingId) });
            }
          }
        } catch (err) {
          console.error("Error fetching pricing:", err);
          setPricingError(err.message || "Failed to fetch pricing options");
        } finally {
          setPricingLoading(false);
        }
      };

      fetchPricing();
    }
  }, [open, getAccessTokenSilently, subscription]);

  const handleClose = () => {
    onFormDataChange({
      customerId: fixedCustomerId || "",
      pricingId: "",
      title: "",
      domain: "",
    });
    onClose();
  };

  const effectiveCustomerId = fixedCustomerId || formData.customerId?.trim() || "";
  const isSuccess = apiResponse?.success === true || (apiResponse?.data != null && !apiResponse?.error);
  const showResult = !!apiResponse;

  const isFormValid = () => {
    return effectiveCustomerId &&
           formData.pricingId && formData.pricingId.trim() &&
           formData.title && formData.title.trim();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>Create Squarespace Order - Single Website</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {subscription && !showResult && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[50],
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight="bold">
              Subscription
            </Typography>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">ID</Typography>
                <Typography variant="body2" fontFamily="monospace" sx={{ display: "block", wordBreak: "break-all" }}>
                  {subscription.id}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">Plan</Typography>
                <Typography variant="body2" sx={{ display: "block" }}>
                  {subscription.plan_details?.name || subscription.plan_legacy || "—"}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Typography variant="body2" sx={{ display: "block" }}>
                  {subscription.status || "—"}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">SS Pricing (from plan)</Typography>
                <Typography variant="body2" fontFamily="monospace" sx={{ display: "block", wordBreak: "break-all" }}>
                  {subscription.plan_details?.meta_data?.squarespace_pricing_id || "—"}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
        {isSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Order created successfully
          </Alert>
        )}
        {showResult && (
          <>
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[100],
                fontFamily: "monospace",
                fontSize: "0.8rem",
                overflow: "auto",
                maxHeight: 200,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                API Response
              </Typography>
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {JSON.stringify(apiResponse, null, 2)}
              </pre>
            </Box>
            {isSuccess && (() => {
              const orderData = apiResponse?.data;
              const orderId = orderData?.orderId ?? orderData?.id;
              const customerId = effectiveCustomerId || orderData?.customerId;
              const pricingId = orderData?.items?.[0]?.pricingId || formData.pricingId;
              if (!orderId && !customerId && !pricingId) return null;
              const metaObj = {
                ...(pricingId && { squarespace_pricing_id: pricingId }),
                ...(customerId && { squarespace_customer_id: customerId }),
                ...(orderId && { squarespace_order_id: orderId }),
              };
              if (Object.keys(metaObj).length === 0) return null;
              return (
                <Box sx={{ mt: 2, p: 2, borderRadius: 1, backgroundColor: "action.hover" }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Link order data in BryteBridge
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    To link this Squarespace order to BryteBridge, add these to meta_data:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
                      fontSize: "0.875rem",
                      fontFamily: "monospace",
                      overflow: "auto",
                    }}
                  >
                    {JSON.stringify(metaObj, null, 2)}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    <strong>1. Subscription Plan</strong> — Add <code style={{ fontFamily: "monospace" }}>squarespace_pricing_id</code> to the plan&apos;s meta_data.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    <strong>2. Subscription</strong> — Go to Subscription detail → Edit meta_data → add <code style={{ fontFamily: "monospace" }}>squarespace_customer_id</code> and <code style={{ fontFamily: "monospace" }}>squarespace_order_id</code> with the values above.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    <strong>3. Organization → Contacts</strong> — Find the contact → Edit meta_data → add <code style={{ fontFamily: "monospace" }}>squarespace_customer_id</code> with the value above.
                  </Typography>
                </Box>
              );
            })()}
          </>
        )}
        {(!showResult || !isSuccess) && (
        <>
        {pricingError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {pricingError}
          </Alert>
        )}
        <Grid container spacing={2}>
        {!fixedCustomerId && (
          <>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Customer Information
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Customer ID"
              value={formData.customerId || ""}
              onChange={(e) => onFormDataChange({ ...formData, customerId: e.target.value })}
              margin="dense"
              size="small"
              disabled={creating}
              required
              helperText="Customer ID (UUID) from Squarespace - Required"
              placeholder="e.g., 2d6563fc-007f-4906-a029-32f7c03799f7"
            />
          </Grid>
          </>
        )}
        {fixedCustomerId && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="text.secondary">
              Customer: <Typography component="span" fontFamily="monospace" variant="body2">{fixedCustomerId}</Typography>
            </Typography>
          </Grid>
        )}

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
              Website Details
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth margin="dense" size="small" disabled={creating || pricingLoading}>
              <InputLabel>Select Website Pricing</InputLabel>
              <Select
                value={
                  pricingLoading || pricingOptions.length === 0
                    ? ""
                    : (formData.pricingId || "")
                }
                onChange={(e) => onFormDataChange({ ...formData, pricingId: e.target.value })}
                label="Select Website Pricing"
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 320 } },
                }}
              >
                {pricingLoading ? (
                  <MenuItem disabled value="">
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Loading pricing options...
                  </MenuItem>
                ) : pricingOptions.length === 0 ? (
                  <MenuItem disabled value="">
                    No pricing options available
                  </MenuItem>
                ) : [
                  ...(formData.pricingId && !pricingOptions.some((p) => String(p.id) === String(formData.pricingId))
                    ? [<MenuItem key="prefilled" value={String(formData.pricingId)}>Pre-filled from plan: {formData.pricingId}</MenuItem>]
                    : []),
                  ...pricingOptions.map((pricing) => {
                    const displayName = pricing.productName || pricing.name || pricing.tld || pricing.extension || pricing.id;
                    const price = pricing.price || pricing.registrationPrice || pricing.monthlyPrice || pricing.annualPrice;
                    const priceDisplay = price ? `$${(price > 10000 ? price / 100 : price).toFixed(2)}` : '';
                    const baseLabel = priceDisplay ? `${displayName} - ${priceDisplay}` : displayName;
                    const label = pricing.id ? `${baseLabel} (${pricing.id})` : baseLabel;
                    const idStr = String(pricing.id);
                    return (
                      <MenuItem key={pricing.id || pricing.tld || pricing.extension} value={idStr}>
                        {label}
                      </MenuItem>
                    );
                  }),
                ]}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Website Title"
              value={formData.title || ""}
              onChange={(e) => onFormDataChange({ ...formData, title: e.target.value })}
              margin="dense"
              size="small"
              disabled={creating}
              required
              helperText="Title for the new website"
              placeholder="e.g., My New Travel Website"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Domain (Optional)"
              value={formData.domain || ""}
              onChange={(e) => onFormDataChange({ ...formData, domain: e.target.value })}
              margin="dense"
              size="small"
              disabled={creating}
              helperText="Domain name for the website (e.g., example.com)"
              placeholder="e.g., example.com"
            />
          </Grid>
        </Grid>
        </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={creating}>
          {isSuccess ? "Done" : "Cancel"}
        </Button>
        {!isSuccess && (
          <Button 
            onClick={onCreate} 
            variant="contained"
            disabled={!isFormValid() || creating}
            startIcon={creating ? <CircularProgress size={16} /> : null}
          >
            {creating ? "Creating..." : "Create Order"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
